import React from "react";
import { Autocomplete, Box, TextField } from "@mui/material";
import { MRT_Row, MRT_ColumnDef } from "material-react-table";
import type { Job, Deal } from "../../../../../../api/types";
import { ImportTableColumn } from "../../../../../../utilities/types";
import { getValidationErrorKey } from "../../../../../../utilities/jobValidation";
import { columnDisplayNames } from "../../../../../admin/ImportSettings";
import { DropdownColumnOptions } from "../../../ImportTable.types";
import { ColumnContext } from "../ColumnContext";
import { useSelectColumnViewModel } from "./selectColumn.vm";
import { SelectColumnViewModel, SelectChangeOptions, SelectBlurOptions } from "./selectColumn.types";
import { useValidationErrors } from "../../../ValidationErrorsContext";
import { useIsRowInEditMode, useEditModeContext } from "../../../EditModeContext";

interface SelectColumnCellProps<T> {
	row: MRT_Row<Job>;
	vm: SelectColumnViewModel<T>;
	fieldKey: keyof Job;
	columnId: ImportTableColumn;
	dropdownOptions?: T[];
	freeSolo?: boolean;
	requiredOption?: boolean | ((allDeals: Deal[]) => boolean);
	getOptionLabel?: (option: T) => string;
	isOptionEqualToValue?: (option: T, value: T) => boolean;
	getValue?: (job: Job) => T | null;
	setValue?: (job: Job, value: T | null) => void;
	updateSubRows?: (row: MRT_Row<Job>, field: keyof Job, newValue: T | null, allDeals: Deal[]) => void;
	isDisabled?: (row: MRT_Row<Job>) => boolean;
	isNumber?: boolean;
}

/**
 * Memoized Cell component for select columns
 */
function SelectColumnCellInner<T>({
	row,
	vm,
	fieldKey,
	columnId,
	dropdownOptions,
	freeSolo = true,
	requiredOption,
	getOptionLabel,
	isOptionEqualToValue,
	getValue,
	setValue,
	updateSubRows,
	isDisabled,
	isNumber = false,
}: SelectColumnCellProps<T>) {
	const job = row.original;
	const validationErrors = useValidationErrors();
	const isInEditMode = useIsRowInEditMode(job.id);
	const { exitRowEdit } = useEditModeContext();
	const currentValue = vm.getCurrentValue(job, fieldKey, getValue);
	const displayValue = vm.getDisplayValue(currentValue, freeSolo);
	const isRequired = vm.getIsRequired(row, requiredOption);
	const disabled = vm.getIsDisabled(row, isDisabled);
	const errorKey = getValidationErrorKey(columnId, job.id);
	const hasError = !!validationErrors?.[errorKey];

	// View Mode: render plain text for performance
	if (!isInEditMode) {
		// Get display label for the current value
		const displayLabel =
			getOptionLabel && currentValue ? getOptionLabel(currentValue as T) : String(displayValue || "");
		return (
			<Box
				title={hasError ? validationErrors[errorKey] : undefined}
				sx={{
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					color: hasError ? "error.main" : "inherit",
					borderBottom: hasError ? "2px solid" : "none",
					borderColor: "error.main",
					width: "100%",
					px: 0.5,
				}}
			>
				{displayLabel || <span style={{ color: "#999" }}>-</span>}
			</Box>
		);
	}

	const changeOptions: SelectChangeOptions<T> = { setValue, updateSubRows };
	const blurOptions: SelectBlurOptions<T> = { isNumber, updateSubRows };

	return (
		<Autocomplete
			size="small"
			freeSolo={freeSolo}
			disabled={disabled}
			style={{ width: "100%", padding: 0 }}
			value={displayValue}
			options={dropdownOptions || []}
			getOptionKey={(option) => {
				if (getOptionLabel) return getOptionLabel(option as T);
				return (option as T)?.toString() || "";
			}}
			getOptionLabel={
				getOptionLabel ? (option) => getOptionLabel(option as T) : (option) => (option as T)?.toString() || ""
			}
			isOptionEqualToValue={isOptionEqualToValue}
			renderInput={(params) => (
				<TextField
					style={{ padding: 0 }}
					variant="outlined"
					required={isRequired}
					error={!!validationErrors?.[errorKey]}
					helperText={validationErrors?.[errorKey]}
					{...params}
					onKeyDown={(e) => {
						if (e.key === "Escape") {
							exitRowEdit(job.id);
						}
					}}
				/>
			)}
			onChange={
				freeSolo
					? undefined
					: (_event, newValue) => {
							if (disabled) return;
							vm.handleSelectChange(job.id, fieldKey, newValue as T | null, row, changeOptions);
						}
			}
			onBlur={
				freeSolo
					? (event) => {
							if (disabled) return;
							const newValue = (event.target as HTMLInputElement).value;
							vm.handleSelectBlur(job.id, fieldKey, newValue, row, blurOptions);
						}
					: undefined
			}
		/>
	);
}

const SelectColumnCell = React.memo(SelectColumnCellInner, (prevProps, nextProps) => {
	const prevJob = prevProps.row.original;
	const nextJob = nextProps.row.original;

	if (prevJob.id !== nextJob.id) return false;
	if (prevJob[prevProps.fieldKey] !== nextJob[nextProps.fieldKey]) return false;
	if (prevProps.columnId !== nextProps.columnId) return false;
	if (prevProps.freeSolo !== nextProps.freeSolo) return false;
	if (prevProps.dropdownOptions !== nextProps.dropdownOptions) return false;
	if (prevProps.vm !== nextProps.vm) return false;

	return true;
}) as typeof SelectColumnCellInner;

interface SelectColumnAggregatedCellProps<T> {
	row: MRT_Row<Job>;
	vm: SelectColumnViewModel<T>;
	fieldKey: keyof Job;
	dropdownOptions?: T[];
	freeSolo?: boolean;
	getOptionLabel?: (option: T) => string;
	isOptionEqualToValue?: (option: T, value: T) => boolean;
	getValue?: (job: Job) => T | null;
	setValue?: (job: Job, value: T | null) => void;
	isNumber?: boolean;
}

/**
 * Memoized AggregatedCell component for select columns
 * Renders editable Autocomplete when group row is in edit mode
 */
function SelectColumnAggregatedCellInner<T>({
	row,
	vm,
	fieldKey,
	dropdownOptions,
	freeSolo = true,
	getOptionLabel,
	isOptionEqualToValue,
	getValue,
	setValue,
	isNumber = false,
}: SelectColumnAggregatedCellProps<T>) {
	// For group rows, check edit mode using row.id (e.g., "grower:Alexander, Curt")
	const isInEditMode = useIsRowInEditMode(row.id);
	const { exitRowEdit } = useEditModeContext();

	// Get value from first job in group
	const job = row.original;
	const currentValue = vm.getCurrentValue(job, fieldKey, getValue);
	const displayValue = vm.getDisplayValue(currentValue, freeSolo);

	// View Mode: render plain text
	if (!isInEditMode) {
		const displayLabel =
			getOptionLabel && currentValue ? getOptionLabel(currentValue as T) : String(displayValue || "");
		return (
			<Box
				sx={{
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					width: "100%",
					px: 0.5,
				}}
			>
				{displayLabel || <span style={{ color: "#999" }}>-</span>}
			</Box>
		);
	}

	// Edit Mode: render Autocomplete that updates all rows in group on change/blur
	const changeOptions: SelectChangeOptions<T> = { setValue };

	return (
		<Autocomplete
			size="small"
			freeSolo={freeSolo}
			style={{ width: "100%", padding: 0 }}
			value={displayValue}
			options={dropdownOptions || []}
			getOptionKey={(option) => {
				if (getOptionLabel) return getOptionLabel(option as T);
				return (option as T)?.toString() || "";
			}}
			getOptionLabel={
				getOptionLabel ? (option) => getOptionLabel(option as T) : (option) => (option as T)?.toString() || ""
			}
			isOptionEqualToValue={isOptionEqualToValue}
			renderInput={(params) => (
				<TextField
					style={{ padding: 0 }}
					variant="outlined"
					{...params}
					onKeyDown={(e) => {
						if (e.key === "Escape") {
							exitRowEdit(row.id);
						}
					}}
				/>
			)}
			onChange={
				freeSolo
					? undefined
					: (_event, newValue) => {
							vm.handleGroupSelectChange(row, fieldKey, newValue as T | null, changeOptions);
						}
			}
			onBlur={
				freeSolo
					? (event) => {
							const newValue = (event.target as HTMLInputElement).value;
							vm.handleGroupSelectBlur(row, fieldKey, newValue, isNumber);
						}
					: undefined
			}
		/>
	);
}

const SelectColumnAggregatedCell = React.memo(SelectColumnAggregatedCellInner, (prevProps, nextProps) => {
	const prevJob = prevProps.row.original;
	const nextJob = nextProps.row.original;

	if (prevJob.id !== nextJob.id) return false;
	if (prevJob[prevProps.fieldKey] !== nextJob[nextProps.fieldKey]) return false;
	if (prevProps.freeSolo !== nextProps.freeSolo) return false;
	if (prevProps.dropdownOptions !== nextProps.dropdownOptions) return false;
	if (prevProps.vm !== nextProps.vm) return false;

	return true;
}) as typeof SelectColumnAggregatedCellInner;

/**
 * Build a dropdown/autocomplete column
 * Uses memoized Cell components to prevent unnecessary re-renders when column definitions change
 */
export function selectColumn<T>(
	context: ColumnContext,
	fieldKey: keyof Job,
	columnId: ImportTableColumn,
	options?: DropdownColumnOptions<T>,
): MRT_ColumnDef<Job, unknown> {
	const {
		isNumber = false,
		options: dropdownOptions,
		hasGroupUpdate = true,
		size,
		freeSolo = true,
		isRequired: requiredOption,
		getOptionLabel,
		isOptionEqualToValue,
		getValue,
		setValue,
		updateSubRows,
		isDisabled,
	} = options || {};

	const vm = useSelectColumnViewModel<T>(context, columnId);
	const displayName = columnDisplayNames[columnId];

	return {
		id: columnId,
		header: displayName,
		accessorKey: fieldKey as string,
		size: size || (fieldKey === "externalFrequencyOfMicros" ? 100 : 160),
		enableEditing: false,
		Cell: ({ row }: { row: MRT_Row<Job> }) => (
			<SelectColumnCell<T>
				row={row}
				vm={vm}
				fieldKey={fieldKey}
				columnId={columnId}
				dropdownOptions={dropdownOptions}
				freeSolo={freeSolo}
				requiredOption={requiredOption}
				getOptionLabel={getOptionLabel}
				isOptionEqualToValue={isOptionEqualToValue}
				getValue={getValue}
				setValue={setValue}
				updateSubRows={updateSubRows}
				isDisabled={isDisabled}
				isNumber={isNumber}
			/>
		),
		...(hasGroupUpdate && {
			AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => (
				<SelectColumnAggregatedCell<T>
					row={row}
					vm={vm}
					fieldKey={fieldKey}
					dropdownOptions={dropdownOptions}
					freeSolo={freeSolo}
					getOptionLabel={getOptionLabel}
					isOptionEqualToValue={isOptionEqualToValue}
					getValue={getValue}
					setValue={setValue}
					isNumber={isNumber}
				/>
			),
		}),
	};
}
